import React from 'react';
import { Sparkles, Video } from 'lucide-react';

/**
 * Winhouse Classroom — Video Panel
 *
 * Rendered ONLY when the booking's video_provider is NOT 'daily'. The classroom
 * shell hides this entirely once the backend lazy-provisions a Daily room.
 *
 * Branches:
 *  - provider==='daily' but provider_room_url missing → 'Preparing your secure link…'
 *  - provider==='stub' (no Daily configured) → Winhouse-branded fallback
 *  - admin/owner viewing get a tech-detail diagnostics chip
 */
const VideoPanel = ({ role, yourName, counterpartName, provider, providerReady, diagnostics }) => {
  const showDiagnostics = ['admin', 'owner'].includes((role || '').toLowerCase()) && diagnostics;
  return (
    <div className="relative w-full h-full min-h-[420px] rounded-2xl overflow-hidden bg-gradient-to-br from-[#0a0a0a] via-[#1a0e0e] to-[#0a0a0a] border border-white/10"
         data-testid="classroom-video-panel">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: 'linear-gradient(rgba(211,216,111,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(211,216,111,.4) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
      }} />

      {/* Brand watermark */}
      <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 border border-white/10 backdrop-blur">
        <Sparkles className="h-3.5 w-3.5 text-[#D3D86F]" />
        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white">Winhouse Classroom</span>
      </div>

      {/* Role badge */}
      <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-[#D3D86F] text-black text-[11px] font-bold uppercase tracking-wider">
        {role}
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
        <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-5">
          <Video className="h-9 w-9 text-[#D3D86F]" />
        </div>
        <div className="text-white text-2xl font-bold mb-1.5">Hello, {yourName}</div>
        <div className="text-white/70 text-sm mb-5">
          Waiting for <span className="font-semibold text-white">{counterpartName}</span> to join.
        </div>
        {!providerReady && (
          <div className="max-w-sm text-[12px] text-white/50 leading-relaxed border-t border-white/10 pt-4 mt-2">
            Live video will activate as soon as Winhouse turns on the video service for your account.
            Until then, you can still use the chat, whiteboard, lesson notes and class resources below.
          </div>
        )}
      </div>

      {/* Admin-only diagnostics */}
      {showDiagnostics && (
        <div className="absolute bottom-16 left-4 right-4 mx-auto max-w-xl text-[10px] font-mono bg-black/60 border border-amber-500/40 rounded-lg p-3 text-amber-100"
             data-testid="video-diagnostics">
          <div className="uppercase tracking-wider font-bold text-amber-300 mb-1">Admin diagnostics (only you see this)</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            <div>env_provider:</div><div>{String(diagnostics.env_provider)}</div>
            <div>api_key_present:</div><div>{String(diagnostics.daily_api_key_present)}</div>
            <div>booking_provider:</div><div>{String(diagnostics.booking_provider)}</div>
            <div>room_created:</div><div>{String(diagnostics.room_created)}</div>
            <div>token_minted:</div><div>{String(diagnostics.token_minted)}</div>
            <div>has_start_end:</div><div>{String(diagnostics.has_start_end)}</div>
            {diagnostics.join_error && <><div>join_error:</div><div className="break-all">{String(diagnostics.join_error)}</div></>}
          </div>
        </div>
      )}

      {/* Mock control bar */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {['mic', 'camera', 'share', 'leave'].map((c) => (
          <div key={c} className={`h-10 px-4 rounded-full flex items-center text-[11px] font-semibold uppercase tracking-wider ${c === 'leave' ? 'bg-red-600 text-white' : 'bg-white/10 text-white border border-white/10'}`}>
            {c}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VideoPanel;
